import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAds, trackAdClick } from '../lib/api';
import { X } from 'lucide-react';

const AdBanner = ({ position = 'sidebar' }) => {
  const [ads, setAds] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    loadAds();
  }, [position]);

  const loadAds = async () => {
    try {
      const res = await getAds(position);
      setAds(res.data);
    } catch (error) {
      console.error('Error loading ads:', error);
    }
  };

  const handleClick = async (ad) => {
    try {
      await trackAdClick(ad.ad_id);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleDismiss = (adId) => {
    setDismissed(prev => [...prev, adId]);
  };

  const visibleAds = ads.filter(ad => !dismissed.includes(ad.ad_id));

  if (visibleAds.length === 0) return null;

  const ad = visibleAds[0];

  if (position === 'sidebar') {
    return (
      <div className="relative bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl overflow-hidden border border-border/50" data-testid="ad-sidebar">
        <button
          onClick={() => handleDismiss(ad.ad_id)}
          className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <Link
          to={ad.link_url}
          onClick={() => handleClick(ad)}
          className="block"
        >
          <img
            src={ad.image_url}
            alt={ad.title}
            className="w-full aspect-[4/3] object-cover"
          />
          <div className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Sponsorisé</p>
            <h4 className="font-semibold text-sm mb-1">{ad.title}</h4>
            {ad.description && (
              <p className="text-xs text-muted-foreground">{ad.description}</p>
            )}
          </div>
        </Link>
      </div>
    );
  }

  if (position === 'inline') {
    return (
      <div className="relative bg-secondary/50 rounded-xl overflow-hidden p-4 flex items-center gap-4" data-testid="ad-inline">
        <button
          onClick={() => handleDismiss(ad.ad_id)}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <img
          src={ad.image_url}
          alt={ad.title}
          className="w-24 h-24 rounded-lg object-cover shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mb-1">Sponsorisé</p>
          <h4 className="font-semibold mb-1">{ad.title}</h4>
          {ad.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{ad.description}</p>
          )}
          <Link
            to={ad.link_url}
            onClick={() => handleClick(ad)}
            className="text-sm text-primary hover:underline mt-2 inline-block"
          >
            En savoir plus →
          </Link>
        </div>
      </div>
    );
  }

  if (position === 'hero') {
    return (
      <div className="relative" data-testid="ad-hero">
        <Link
          to={ad.link_url}
          onClick={() => handleClick(ad)}
          className="block bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow"
        >
          <p className="text-xs text-muted-foreground mb-2">Sponsorisé</p>
          <h4 className="font-heading font-semibold text-lg mb-2">{ad.title}</h4>
          {ad.description && (
            <p className="text-muted-foreground text-sm">{ad.description}</p>
          )}
        </Link>
      </div>
    );
  }

  return null;
};

export default AdBanner;
